import { useState } from 'react'
import { Input, Button, Upload, Space, message as antMessage } from 'antd'
import { SendOutlined, PaperClipOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'

const { TextArea } = Input

interface MessageInputProps {
  onSend: (content: string, attachments?: UploadFile[]) => Promise<{ success: boolean; error?: string }>
  placeholder?: string
  loading?: boolean
  allowAttachments?: boolean
  maxLength?: number
}

export default function MessageInput({ 
  onSend, 
  placeholder = "Type your message...",
  loading = false,
  allowAttachments = false,
  maxLength = 2000
}: MessageInputProps) {
  const [content, setContent] = useState('')
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [sending, setSending] = useState(false)

  const handleSend = async () => {
    if (!content.trim() && fileList.length === 0) {
      antMessage.warning('Please enter a message or attach a file')
      return
    }

    setSending(true)
    try {
      const result = await onSend(content.trim(), fileList)
      
      if (result.success) {
        setContent('')
        setFileList([])
        antMessage.success('Message sent successfully')
      } else {
        antMessage.error(result.error || 'Failed to send message')
      }
    } catch {
      antMessage.error('An error occurred while sending the message')
    } finally {
      setSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const uploadProps = {
    fileList,
    beforeUpload: (file: UploadFile) => {
      // Validate file size (max 10MB)
      const isLt10M = (file.size || 0) / 1024 / 1024 < 10
      if (!isLt10M) {
        antMessage.error('File must be smaller than 10MB!')
        return false
      }

      // Add to file list
      setFileList([...fileList, file])
      return false // Prevent auto upload
    },
    onRemove: (file: UploadFile) => {
      setFileList(fileList.filter(f => f.uid !== file.uid))
    }
  }

  return (
    <div className="message-input-container bg-white dark:bg-gray-800 p-4 border-t border-gray-200 dark:border-gray-700">
      <Space.Compact className="w-full" direction="vertical" size="middle">
        {allowAttachments && fileList.length > 0 && (
          <Upload {...uploadProps} listType="picture-card">
            {/* File list will be shown automatically */}
          </Upload>
        )}
        
        <Space.Compact className="w-full">
          <TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            autoSize={{ minRows: 1, maxRows: 4 }}
            maxLength={maxLength}
            showCount
            disabled={loading || sending}
            className="flex-1"
          />
          
          <Space.Compact>
            {allowAttachments && (
              <Upload {...uploadProps} showUploadList={false}>
                <Button 
                  icon={<PaperClipOutlined />} 
                  disabled={loading || sending}
                  title="Attach file"
                />
              </Upload>
            )}
            
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={sending}
              disabled={loading || (!content.trim() && fileList.length === 0)}
              title="Send message"
            >
              Send
            </Button>
          </Space.Compact>
        </Space.Compact>
      </Space.Compact>
    </div>
  )
}